import React from 'react';
import { Link } from 'react-router-dom';

type FooterProps = {
  onDownloadClick?: React.MouseEventHandler<HTMLAnchorElement>;
};

const Footer: React.FC<FooterProps> = () => {
  return (
    <footer
      style={{
        background: 'linear-gradient(180deg, #0A1210 0%, #060A09 100%)',
        borderTop: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      {/* CTA Banner */}
      <div
        className="relative overflow-hidden"
        style={{
          background: 'radial-gradient(ellipse 80% 100% at 50% 0%, rgba(104,193,174,0.15) 0%, transparent 70%)',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
        }}
      >
        <div className="container mx-auto py-20 text-center">
          <h2 className="text-4xl md:text-5xl font-extrabold text-white tracking-tight" style={{ letterSpacing: '-0.03em' }}>
            Make your wrist{' '}
            <span className="gradient-text">unforgettable.</span>
          </h2>
          <p className="mt-5 text-lg text-white/45 max-w-md mx-auto">
            Join 50,000+ Smart Watch fans who've already transformed their daily glance.
          </p>
          <a
            href="https://apps.apple.com/us/app/watchi-watch-faces-gallery/id6753596004"
            className="btn-primary mt-8 inline-flex text-base"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
            </svg>
            Get Watchi — It's Free
          </a>
          <p className="mt-3 text-sm text-white/25">App Store · Free Download · iOS 17+</p>
        </div>
      </div>

      {/* Footer links */}
      <div className="container mx-auto py-14">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-12">
          {/* Brand */}
          <div className="max-w-xs">
            <Link to="/" className="text-xl font-bold text-white tracking-tight">
              Watchi<span className="text-[#68C1AE]">.</span>app
            </Link>
            <p className="mt-3 text-sm text-white/40 leading-relaxed">
              Personalize your Smart Watch with curated faces, animated wallpapers, and interactive widgets.
            </p>
          </div>

          {/* Links */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8 text-sm">
            <div>
              <p className="text-white/30 uppercase tracking-widest text-[11px] font-bold mb-4">Explore</p>
              <ul className="space-y-3">
                <li><a href="#features" className="text-white/55 hover:text-[#68C1AE] transition-colors">Features</a></li>
                <li><a href="#gallery" className="text-white/55 hover:text-[#68C1AE] transition-colors">Gallery</a></li>
                <li><a href="#creators" className="text-white/55 hover:text-[#68C1AE] transition-colors">Creators</a></li>
              </ul>
            </div>
            <div>
              <p className="text-white/30 uppercase tracking-widest text-[11px] font-bold mb-4">Legal</p>
              <ul className="space-y-3">
                <li><Link to="/privacy" className="text-white/55 hover:text-[#68C1AE] transition-colors">Privacy</Link></li>
                <li><Link to="/terms" className="text-white/55 hover:text-[#68C1AE] transition-colors">Terms</Link></li>
                <li><Link to="/cancellation" className="text-white/55 hover:text-[#68C1AE] transition-colors">Cancellation</Link></li>
              </ul>
            </div>
            <div>
              <p className="text-white/30 uppercase tracking-widest text-[11px] font-bold mb-4">Connect</p>
              <ul className="space-y-3">
                <li><a href="mailto:support@watchi.app" className="text-white/55 hover:text-[#68C1AE] transition-colors">Contact</a></li>
                <li><a href="https://instagram.com" target="_blank" rel="noreferrer" className="text-white/55 hover:text-[#68C1AE] transition-colors">Instagram</a></li>
                <li><a href="https://tiktok.com" target="_blank" rel="noreferrer" className="text-white/55 hover:text-[#68C1AE] transition-colors">TikTok</a></li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex flex-col md:flex-row justify-between gap-3 text-sm text-white/25">
            <p>© {new Date().getFullYear()} Watchi.app — Crafted for Smart Watch fans worldwide.</p>
            <p>Made with ♥ in Europe.</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
